import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AttractionDirectoryTable from '../components/directory/AttractionDirectoryTable';
import DirectorySection from '../components/directory/DirectorySection';
import SiteHeader from '../components/directory/SiteHeader';
import FoodInfo from '../components/food/FoodInfo';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PageContainer from '../components/layout/PageContainer';
import { fetchCountryById } from '../services/api';
import { getCountryCityGroups, getDirectoryCountry } from '../services/dataStore';
import { CityPass, Country, RegionalPass } from '../types';
import { useLocale } from '../context/LocaleContext';
import { countryLandmarkEmoji } from '../utils/countryLandmark';
import { getVisaDescriptionForDisplay, isValidUrl } from '../utils/countryPageHelpers';
import { isSupportedLocale } from '../utils/lang-route';
import { t } from '../utils/i18n';

const CountryDirectoryPage: React.FC = () => {
  const { lang, id } = useParams<{ lang: string; id: string }>();
  const { locale, setLocale } = useLocale();
  const effectiveLocale = isSupportedLocale(lang) ? lang : locale;
  const isEn = effectiveLocale === 'en';

  const [country, setCountry] = useState<Country | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pickText = (zh?: string, en?: string) => (isEn ? en || zh || '' : zh || en || '');
  const pickList = (zh?: string[], en?: string[]) => (isEn ? en || zh || [] : zh || en || []);

  useEffect(() => {
    if (isSupportedLocale(lang) && lang !== locale) {
      setLocale(lang);
    }
  }, [lang, locale, setLocale]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    void fetchCountryById(id, effectiveLocale).then((res) => {
      if (res.success && res.data) {
        setCountry(res.data);
      } else {
        setError(res.message || (isEn ? 'Failed to load country info' : '获取国家信息失败'));
      }
      setLoading(false);
    });
  }, [id, effectiveLocale, isEn]);

  const directory = useMemo(
    () => (id ? getDirectoryCountry(id, effectiveLocale) : null),
    [id, effectiveLocale]
  );

  const cityGroups = useMemo(
    () => (id ? getCountryCityGroups(id, effectiveLocale) : null),
    [id, effectiveLocale]
  );

  useEffect(() => {
    if (!cityGroups?.length) return;
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash.startsWith('city-')) return;
    const slug = hash.slice('city-'.length);
    requestAnimationFrame(() => {
      document.getElementById(`city-${slug}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [cityGroups, loading]);

  if (loading) {
    return (
      <PageContainer>
        <SiteHeader />
        <div className="flex justify-center py-24">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error || !country || !directory || !cityGroups) {
    return (
      <PageContainer>
        <SiteHeader />
        <p className="text-[var(--dir-muted)] mt-8">{error || (isEn ? 'Country not found.' : '未找到该国家。')}</p>
        <Link to={`/${effectiveLocale}`} className="dir-link-coral text-sm mt-4 inline-block">
          ← {isEn ? 'Home' : '返回首页'}
        </Link>
      </PageContainer>
    );
  }

  const countryName = isEn ? country.nameEn?.trim() || country.name : country.name;
  const stats = isEn
    ? `${directory.attractionCount} official attraction websites · ${cityGroups.length} cities`
    : `${directory.attractionCount} 个景点官网 · ${cityGroups.length} 个城市`;

  const visaDescriptionDisplay = country.visa
    ? getVisaDescriptionForDisplay(
        country.visa.description,
        country.visa.type,
        country.visa.schengen
      )
    : undefined;

  return (
    <PageContainer>
      <SiteHeader />
      <nav className="text-sm text-[var(--dir-muted)] mt-6 mb-4">
        <Link to={`/${effectiveLocale}`} className="dir-link">
          {isEn ? 'Home' : '首页'}
        </Link>
        <span className="mx-2">/</span>
        <span>{countryName}</span>
      </nav>

      <div className="flex items-start gap-3 mb-1">
        <span className="text-3xl">{country.flag}</span>
        <span className="text-2xl">{countryLandmarkEmoji(country.id)}</span>
      </div>
      <h1 className="text-3xl font-semibold tracking-tight">{countryName}</h1>
      {!isEn && country.nameEn ? (
        <p className="text-[var(--dir-muted)] mt-1">{country.nameEn}</p>
      ) : null}
      <p className="text-sm text-[var(--dir-muted)] mt-2">
        {t(effectiveLocale, 'popularity')}: {country.popularity} · {t(effectiveLocale, 'continent')}:{' '}
        {country.continent}
      </p>
      <p className="text-[var(--dir-muted)] mt-1 mb-10">{stats}</p>

      {!isEn && country.visa && (
        <DirectorySection title={t(effectiveLocale, 'visa')} icon="🛂">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex px-2.5 py-1 rounded-md text-sm border border-[var(--dir-border)] bg-[var(--dir-bg)]">
                  {country.visa.type === 'visa-free'
                    ? `✅ ${t(effectiveLocale, 'visaFree')}`
                    : country.visa.type === 'visa-on-arrival'
                      ? `🛂 ${t(effectiveLocale, 'visaOnArrival')}`
                      : country.visa.type === 'visa-required'
                        ? `📋 ${t(effectiveLocale, 'visaRequired')}`
                        : t(effectiveLocale, 'visaInfo')}
                </span>
                {country.visa.schengen ? (
                  <span className="inline-flex px-2.5 py-1 rounded-md text-sm border border-[var(--dir-border)]">
                    🇪🇺 {t(effectiveLocale, 'schengen')}
                  </span>
                ) : null}
              </div>
              {visaDescriptionDisplay ? (
                <p className="text-sm text-[var(--dir-muted)]">{visaDescriptionDisplay}</p>
              ) : null}
              {country.visa.jurisdiction ? (
                <p className="text-sm text-[var(--dir-muted)]">
                  {t(effectiveLocale, 'jurisdiction')}：{country.visa.jurisdiction}
                </p>
              ) : null}
              {country.visa.warnings?.map((warning, idx) => (
                <p key={idx} className="text-sm text-red-700">
                  ⚠️ {warning}
                </p>
              ))}
            </div>
            <div className="flex items-center justify-center md:justify-end">
              {country.visa.type === 'visa-required' && country.visa.officialUrl ? (
                <a
                  href={country.visa.officialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-medium bg-[var(--dir-coral)] text-white hover:opacity-90"
                >
                  {t(effectiveLocale, 'applyVisa')} ↗
                </a>
              ) : country.visa.type === 'visa-free' ? (
                <p className="text-sm text-[var(--dir-muted)]">{t(effectiveLocale, 'noVisa')}</p>
              ) : country.visa.type === 'visa-on-arrival' ? (
                <p className="text-sm text-[var(--dir-muted)]">{t(effectiveLocale, 'applyOnArrival')}</p>
              ) : (
                <p className="text-sm text-[var(--dir-muted)]">{t(effectiveLocale, 'contactEmbassy')}</p>
              )}
            </div>
          </div>
        </DirectorySection>
      )}

      {country.travelInfo && (
        <DirectorySection title={t(effectiveLocale, 'travelInfo')} icon="✈️">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <div>
              <h3 className="text-sm font-semibold mb-2">{t(effectiveLocale, 'bestTime')}</h3>
              <p className="text-sm text-[var(--dir-muted)]">
                {pickList(country.travelInfo.bestMonths, country.travelInfo.bestMonthsEn).join(
                  isEn ? ', ' : '、'
                ) || '—'}
              </p>
              <p className="text-sm text-[var(--dir-muted)] mt-1">
                {t(effectiveLocale, 'peakSeason')}：
                {pickText(country.travelInfo.peakSeason, country.travelInfo.peakSeasonEn) || '—'}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-2">{t(effectiveLocale, 'climateInfo')}</h3>
              <p className="text-sm text-[var(--dir-muted)]">
                {pickText(country.travelInfo.climateInfo, country.travelInfo.climateInfoEn) ||
                  t(effectiveLocale, 'noTravelInfo')}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-2">{t(effectiveLocale, 'notices')}</h3>
              {pickList(country.travelInfo.recentNotices, country.travelInfo.recentNoticesEn).length >
              0 ? (
                <ul className="text-sm text-red-700 space-y-1">
                  {pickList(country.travelInfo.recentNotices, country.travelInfo.recentNoticesEn).map(
                    (notice, index) => (
                      <li key={index}>⚠️ {notice}</li>
                    )
                  )}
                </ul>
              ) : (
                <p className="text-sm text-[var(--dir-muted)]">—</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-[var(--dir-border)]">
            <div>
              <h3 className="text-sm font-semibold mb-2">{t(effectiveLocale, 'currency')}</h3>
              <p className="text-sm text-[var(--dir-muted)]">
                {country.travelInfo.currency || t(effectiveLocale, 'noCurrencyInfo')}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-2">{t(effectiveLocale, 'language')}</h3>
              <p className="text-sm text-[var(--dir-muted)]">
                {country.travelInfo.languages?.length
                  ? country.travelInfo.languages.join(isEn ? ', ' : '、')
                  : t(effectiveLocale, 'noLanguageInfo')}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-2">{t(effectiveLocale, 'timezone')}</h3>
              <p className="text-sm text-[var(--dir-muted)]">
                {country.travelInfo.timezone || t(effectiveLocale, 'noTimezoneInfo')}
              </p>
            </div>
          </div>
        </DirectorySection>
      )}

      <section className="mb-12">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--dir-muted)] mb-6 flex items-center gap-2">
          <span>🎫</span>
          {t(effectiveLocale, 'attractions')}
        </h2>
        <div className="space-y-10">
          {cityGroups.map((group) => (
            <div key={group.slug} id={`city-${group.slug}`} className="scroll-mt-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                <h3 className="text-lg font-semibold">{group.cityDisplayName}</h3>
                <span className="text-sm text-[var(--dir-muted)]">
                  {isEn
                    ? `${group.attractions.length} attractions`
                    : `${group.attractions.length} 个景点`}
                </span>
              </div>
              <AttractionDirectoryTable attractions={group.attractions} />
            </div>
          ))}
        </div>
      </section>

      {country.regionalPasses && country.regionalPasses.length > 0 && (
        <DirectorySection title={t(effectiveLocale, 'regionalPasses')} icon="🎟️">
          <div className="space-y-4">
            {country.regionalPasses.map((pass: RegionalPass) => (
              <div
                key={pass.id}
                className="pb-4 border-b border-[var(--dir-border)] last:border-0 last:pb-0"
              >
                <div className="font-medium">{pass.name}</div>
                {pass.description ? (
                  <p className="text-sm text-[var(--dir-muted)] mt-1">{pass.description}</p>
                ) : null}
                <p className="text-xs text-[var(--dir-muted)] mt-1">
                  {pass.price ? <span className="mr-3">{pass.price}</span> : null}
                  {pass.validity ? (
                    <span>
                      {t(effectiveLocale, 'validity')}：{pass.validity}
                    </span>
                  ) : null}
                </p>
                {pass.officialUrl && isValidUrl(pass.officialUrl) ? (
                  <a
                    href={pass.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dir-link-coral text-sm font-medium inline-block mt-2"
                  >
                    {t(effectiveLocale, 'buyPass')} ↗
                  </a>
                ) : pass.officialUrl ? (
                  <p className="text-xs text-[var(--dir-muted)] mt-1">{pass.officialUrl}</p>
                ) : null}
              </div>
            ))}
          </div>
        </DirectorySection>
      )}

      {country.cityPasses && country.cityPasses.length > 0 && (
        <DirectorySection
          title={`${t(effectiveLocale, 'cityPasses')} (${country.cityPasses.length} ${t(effectiveLocale, 'cities')})`}
          icon="🚇"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {country.cityPasses.map((pass: CityPass) => (
              <div
                key={pass.id}
                className="rounded-lg border border-[var(--dir-border)] p-4 bg-[var(--dir-bg)]"
              >
                <div className="font-semibold text-[var(--dir-text)]">{pass.cityName}</div>
                {pass.cityNameEn ? (
                  <div className="text-sm text-[var(--dir-muted)]">{pass.cityNameEn}</div>
                ) : null}
                {pass.transportation ? (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-[var(--dir-muted)] mb-1">
                      🚇 {t(effectiveLocale, 'transportation')}
                    </div>
                    <p className="text-sm text-[var(--dir-muted)] leading-relaxed">{pass.transportation}</p>
                  </div>
                ) : null}
                {pass.recommendation ? (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-[var(--dir-muted)] mb-1">
                      💡 {t(effectiveLocale, 'whereToBuy')}
                    </div>
                    <p className="text-sm text-[var(--dir-muted)] leading-relaxed">{pass.recommendation}</p>
                  </div>
                ) : null}
                {pass.buyTicket ? (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-[var(--dir-muted)] mb-1">
                      💰 {t(effectiveLocale, 'buySuggestion')}
                    </div>
                    <p className="text-sm text-[var(--dir-muted)] leading-relaxed">{pass.buyTicket}</p>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </DirectorySection>
      )}

      <FoodInfo
        food={country.foodInfo?.food}
        pricePerMeal={country.foodInfo?.pricePerMeal}
        tips={country.foodInfo?.tips}
        notification={country.foodInfo?.notification}
        findLocalFood={country.foodInfo?.findLocalFood}
      />

      <footer className="mt-12 pt-8 border-t border-[var(--dir-border)] text-center text-sm text-[var(--dir-muted)]">
        <p>{t(effectiveLocale, 'footerTagline')}</p>
        <p className="mt-2">{t(effectiveLocale, 'footerOfficialOnly')}</p>
        <p className="mt-2 text-xs">
          {t(effectiveLocale, 'footerReportIssue')}
          <a href="mailto:reneewu86@outlook.com" className="dir-link-coral ml-1">
            reneewu86@outlook.com
          </a>
        </p>
      </footer>
    </PageContainer>
  );
};

export default CountryDirectoryPage;
